import React from 'react';

import { Box, Flex, Tooltip } from '@chakra-ui/react';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import TextWithEllipsis from 'components/TextWithEllipsis';
import useOKRConfig from 'components/hooks/useOKRConfig';

import {
  ArrowUp1,
  KeyResultIcon,
  ObjectiveTypeCompany,
  ObjectiveTypeDepartment,
  ObjectiveTypeIndividual,
  ObjectiveTypeTeam,
  KeyResultPrivate2 as PrivateKeyResultIcon,
  PrivateObjective as PrivateObjectiveIcon,
} from 'assets/assetsV1/iconComponents';
import { Objective as ObjectiveIcon } from 'assets/assetsV1/iconComponents/Objective';

import {
  COLOR_MAP_FULL,
  GOAL_VISIBILITY_OPTIONS,
  OBJECTIVE_LEVEL_TAGS,
  OBJECTIVE_TYPES_MAP,
} from 'constants/ProjectConstants';
import { minifyNumber } from 'constants/numberMinifyHelper';

import { Avatar } from 'uiToolkitV1/Avatar';
import { AvatarGroup } from 'uiToolkitV1/AvatarGroup';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

import { OWNER_TYPES } from '../PrimaryPanel/Sections/Details/OKROwnerAndCollaborator';
import TooltipLabelList from '../PrimaryPanel/Sections/Details/TooltipLabelList';
import { getSelectedOption, StatusTag } from '../PrimaryPanel/Sections/Overview/OKRStatus';
import { OKR_TYPES } from '../PrimaryPanel/Sections/Overview/ParentOKR';

const StyledContainer = styled(Flex)`
  align-items: flex-start;
  gap: 4px;

  border-radius: 8px;
  padding: 12px 8px;
  padding-left: ${(props) => props.paddingLeft ?? '8px'};

  background: ${(props) => (props.isHighlighted ? props.theme.colors.surface.E6F1FD : 'inherit')};
  &:hover {
    background: ${(props) => props.theme.colors.surface.E6F1FD};
    & .highlightTextOnHover {
      color: #1565bc;
    }
  }
  cursor: pointer;

  > :first-child {
    flex: auto 0 0;
  }

  > :last-child {
    flex: auto 1 1;
  }
`;

const RightArrow = styled(ArrowUp1)`
  margin-top: 6px;
  transform: rotate(90deg);
`;

export const getObjectiveLevelIcon = (objectiveLevel) => {
  switch (objectiveLevel) {
    case 'COMPANY': {
      return ObjectiveTypeCompany;
    }
    case 'DEPARTMENT': {
      return ObjectiveTypeDepartment;
    }
    case 'TEAM': {
      return ObjectiveTypeTeam;
    }
    case 'INDIVIDUAL': {
      return ObjectiveTypeIndividual;
    }
    default:
      return null;
  }
};

const OKR_LEVEL_MAP = OBJECTIVE_LEVEL_TAGS.reduce(
  (prev, current) => ({
    ...prev,
    [current.value]: {
      ...current,
      icon: getObjectiveLevelIcon(current.value),
    },
  }),
  {}
);

export const OKRCardComponent = ({
  isCurrentOKR = false,
  onClick = () => {},
  title = '',
  type = OKR_TYPES.OBJECTIVE,
  objectiveType = null,
  toIds = [],
  unMasked = true,
  avgProgressPercentage,
  visibilityType,
  progressLevel,
  closureStatus,
  level = 0,
}) => {
  const theme = useTheme();

  const isObjective = type === OKR_TYPES.OBJECTIVE;
  const isPrivateObjective = isObjective && visibilityType === GOAL_VISIBILITY_OPTIONS[0].value;
  const isPublicObjective = isObjective && visibilityType === GOAL_VISIBILITY_OPTIONS[1].value;
  const isPrivateKR = !isObjective && visibilityType === GOAL_VISIBILITY_OPTIONS[0].value;
  const isPublicKR = !isObjective && visibilityType === GOAL_VISIBILITY_OPTIONS[1].value;

  const ObjectiveTypeIcon = objectiveType ? OKR_LEVEL_MAP[objectiveType]?.icon : null;
  const objectiveTypeLabel = objectiveType ? OKR_LEVEL_MAP[objectiveType]?.label : '';

  /* 
    config based changes
    requirement: for simple objective type hide owners and collaborators field.
  */
  const { OBJECTIVE_TYPE } = useOKRConfig();

  const isSimpleObjectiveType = OBJECTIVE_TYPE === OBJECTIVE_TYPES_MAP.SIMPLE.value;

  const hideOwnersAndCollaborators = isObjective && isSimpleObjectiveType;

  /* config based changes end */

  if (!unMasked) return null;

  const progressStatus = getSelectedOption({
    goalClosureStatus: closureStatus,
    goalProgressLevel: progressLevel,
  });

  return (
    <StyledContainer
      isHighlighted={isCurrentOKR}
      onClick={onClick}
      paddingLeft={level === 0 ? '8px' : `${8 + 16 * (level - 1)}px`}
    >
      {level > 0 ? <RightArrow boxSize="12px" color="#999999" /> : null}
      <Flex alignItems="flex-start">
        <Box flexGrow={0} flexShrink={0}>
          {isPublicObjective ? <ObjectiveIcon color="#7651BC" boxSize="24px" /> : null}
          {isPrivateObjective ? <PrivateObjectiveIcon color="#7651BC" boxSize="24px" /> : null}
          {isPrivateKR ? <PrivateKeyResultIcon color="#4657AE" boxSize="24px" /> : null}
          {isPublicKR ? <KeyResultIcon color="#4657AE" boxSize="24px" /> : null}
        </Box>
        <Flex flexDirection="column" flexGrow={1} flexShrink={1} pl={1}>
          <Box pb={1}>
            <TextWithEllipsis linesToShow={3} tooltipText={title}>
              <TextBodyMedium className="highlightTextOnHover">{title}</TextBodyMedium>
            </TextWithEllipsis>
          </Box>
          <Box>
            {isObjective && ObjectiveTypeIcon !== null ? (
              <Flex py={1} alignItems="center" ml="-4px">
                <ObjectiveTypeIcon color="#999999" boxSize="24px" />
                <TextLabel color="#333333" pl={1}>
                  {objectiveTypeLabel}
                </TextLabel>
              </Flex>
            ) : null}
          </Box>
          {hideOwnersAndCollaborators ? null : (
            <Flex alignItems="center" justifyContent="space-between">
              <Tooltip label={<TooltipLabelList labelList={toIds} />}>
                <Box>
                  <AvatarGroup>
                    {toIds
                      .filter(({ ownerType }) => ownerType === OWNER_TYPES.OWNER)
                      .map(({ id, displayName, profilePhoto }) => (
                        <Avatar
                          key={id}
                          name={displayName}
                          src={profilePhoto}
                          showBorder
                          borderColor={theme.colors.functional['2F8133']}
                          mr="4px !important"
                        />
                      ))}
                    {toIds
                      .filter(({ ownerType }) => ownerType === OWNER_TYPES.COLLABORATOR)
                      .map(({ id, displayName, profilePhoto }) => (
                        <Avatar key={id} name={displayName} src={profilePhoto} mr="4px !important" />
                      ))}
                  </AvatarGroup>
                </Box>
              </Tooltip>
              <Flex alignItems="center">
                <StatusTag
                  mr="8px"
                  bg={COLOR_MAP_FULL[closureStatus ? `${progressLevel}_${closureStatus}` : progressLevel]?.tagColor}
                >
                  <TextLabel
                    bold
                    color={
                      COLOR_MAP_FULL[closureStatus ? `${progressLevel}_${closureStatus}` : progressLevel]?.textColor
                    }
                    mr={1}
                  >
                    {progressStatus?.label}
                  </TextLabel>
                </StatusTag>

                <TextBodyMedium bold ml={1}>
                  {minifyNumber(avgProgressPercentage)}%
                </TextBodyMedium>
              </Flex>
            </Flex>
          )}
        </Flex>
      </Flex>
    </StyledContainer>
  );
};
